import { useEffect, useState } from "react";
import { playSfx } from "../lib/sfx";

// Public toggle for the in-app radio (background music + ad rotation).
// Lives in Home's top bar next to the notifications bell. State
// persists across reloads in localStorage; BackgroundMusic.jsx
// subscribes to the same key + a custom event so the change applies
// instantly without a page reload.
//
// Defaults ON — first-time users get the radio + ads immediately so
// they hear the feature-teaching voiceovers. A user who mutes once
// stays muted until they tap again.

const RADIO_STATE_KEY = "radio:enabled:v1";

export function isRadioEnabled() {
  if (typeof localStorage === "undefined") return true;
  try {
    const v = localStorage.getItem(RADIO_STATE_KEY);
    if (v === "false") return false;
    return true;  // default ON when nothing is set
  } catch { return true; }
}

export function setRadioEnabled(value) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(RADIO_STATE_KEY, value ? "true" : "false");
    window.dispatchEvent(new CustomEvent("radio:toggle", { detail: { enabled: value } }));
  } catch { /* ignore */ }
}

export default function RadioToggle() {
  const [enabled, setEnabled] = useState(() => isRadioEnabled());

  // Cross-tab sync: another tab muting the radio should reflect here
  // too. The custom event covers same-tab; the storage event covers
  // cross-tab.
  useEffect(() => {
    const onCustom = (e) => setEnabled(e?.detail?.enabled ?? isRadioEnabled());
    const onStorage = (e) => {
      if (e.key === RADIO_STATE_KEY) setEnabled(isRadioEnabled());
    };
    window.addEventListener("radio:toggle", onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("radio:toggle", onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const onClick = () => {
    const next = !enabled;
    // Cue the toggle with the matching click — radio-on for unmute,
    // radio-off for mute. Plays through the sfx bus so it lands even
    // while the music is in the middle of fading out.
    playSfx(next ? "radio-on" : "radio-off");
    setEnabled(next);
    setRadioEnabled(next);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      title={enabled ? "Mute the radio" : "Turn the radio on"}
      aria-label={enabled ? "Mute the radio" : "Turn the radio on"}
      aria-pressed={enabled}
      style={{
        // 48px hit-target matches the bell + avatar in the top bar
        // so the three controls read as a row. The radio image scales
        // INSIDE this fixed bounds so taps stay easy to land even
        // when the muted state shrinks the icon.
        background: "transparent",
        border: "none",
        padding: 0,
        width: 48,
        height: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      <img
        src="/img/radio.webp"
        alt=""
        aria-hidden="true"
        style={{
          width: enabled ? 48 : 32,
          height: enabled ? 48 : 32,
          objectFit: "contain",
          // Color story: ON = full saturation, full size. OFF =
          // grayscale + slightly dim + smaller, signalling "this is
          // off but a tap brings it back to life." Transition runs
          // on every property so the toggle feels physical.
          filter: enabled ? "saturate(1)" : "saturate(0) brightness(0.85)",
          opacity: enabled ? 1 : 0.6,
          transition: "width 0.22s ease, height 0.22s ease, filter 0.22s ease, opacity 0.22s ease",
        }}
      />
    </button>
  );
}
